import { ReactElement } from 'react'
import { Box, Checkbox, Divider, FormControlLabel, Stack, useTheme } from '@mui/material'
import { ifcFilterTypeLabels } from '@editor/components/Controls/IFCUploadWidget/constants/ifcFilter'
import { SelectIconButton } from '@ui/actions'
import { useControlStore } from '@editorStores'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import useIfcElementTypeSelection from './useIfcElementTypeSelection'

/**
 * IFC element types that are not covered by the per-storey IW/AW/D/DA buttons. Their visibility is
 * global (all storeys) and toggled through this checkbox list.
 */
export const IFC_OTHER_TYPES = [
  'columns',
  'beams',
  'openings',
  'members',
  'doors',
  'windows',
  'furniture',
  'stairs',
  'property',
  'unclassified',
] as const

/**
 * Checkbox list for the remaining IFC element types (Stützen, Träger, …), rendered inside the IFC
 * filter popover. Each row shows the type's scene color and a checkbox; toggling shows/hides that
 * type globally via `glbTypeVisibility` (mirrors the "Alle SW" per-type toggles for the main types).
 */
const IFCOtherTypesFilter = (): ReactElement => {
  const theme = useTheme()
  const ifcPalette = theme.scenePalette.ifc as Record<string, string>

  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const setGlbTypeVisibility = useControlStore(state => state.setGlbTypeVisibility)

  const { selectIfcByType } = useIfcElementTypeSelection()

  const visibleTypes = IFC_OTHER_TYPES.filter(type => glbTypeVisibility[type] !== false)
  const allVisible = visibleTypes.length === IFC_OTHER_TYPES.length
  const someVisible = visibleTypes.length > 0 && !allVisible

  return (
    <Stack direction="column" sx={{ minWidth: 160 }}>
      <FormControlLabel
        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
        control={
          <Checkbox
            checked={allVisible}
            indeterminate={someVisible}
            size="small"
            sx={{ p: 0, mr: 0.5 }}
            onChange={() =>
              IFC_OTHER_TYPES.forEach(type => setGlbTypeVisibility(type, !allVisible))
            }
            data-cy="ifc-other-type-select-all"
          />
        }
        label="Alle auswählen"
      />
      <Divider sx={{ my: 0.5 }} />
      {IFC_OTHER_TYPES.map(type => (
        <Stack key={type} direction="row" alignItems="center" justifyContent="space-between">
          <FormControlLabel
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
            control={
              <Checkbox
                checked={visibleTypes.includes(type)}
                size="small"
                sx={{ p: 0, mr: 0.5 }}
                onChange={event => setGlbTypeVisibility(type, event.target.checked)}
                data-cy={`ifc-other-type-${type}`}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    border: '1px solid',
                    borderColor: 'grey.400',
                    mr: 0.75,
                    bgcolor: ifcPalette[type] ?? 'transparent',
                  }}
                />
                {ifcFilterTypeLabels[type]}
              </Box>
            }
          />
          <SelectIconButton
            size="small"
            onClick={event =>
              selectIfcByType(type, { extend: eventMatchesPointerModifier(event, 'multiSelect') })
            }
            sx={{ padding: 0, width: 18, height: 18, ml: 1 }}
            data-cy={`ifc-other-type-${type}-select`}
          />
        </Stack>
      ))}
    </Stack>
  )
}

export default IFCOtherTypesFilter
